/**
 * Invite links arrive as /signup?email=…&token=… or /signin?token=…. The token
 * is a credential: with it, whoever holds the link joins the team at signup.
 * Left in the address bar it went to every analytics vendor in the page URL
 * (GA stores the landing URL on its tracker, so every later hit carried it too).
 *
 * `stashInviteToken` runs before any of those tools load (it is the first import
 * in index.tsx). It moves the token into this tab's sessionStorage and strips it
 * from the URL. The auth pages read it back with `readInviteToken`, so a reload
 * still finds it.
 */
const STORAGE_KEY = "equility.inviteToken";
const INVITE_PATHS = ["/signup", "/signin"];

const normalize = (path: string) => path.replace(/\/+$/, "") || "/";

export function stashInviteToken(): void {
	try {
		const url = new URL(window.location.href);
		const token = url.searchParams.get("token");
		if (!token || !INVITE_PATHS.includes(normalize(url.pathname))) return;
		// Store first: if storage is blocked this throws and the URL keeps the
		// token, so the invite still works (it just stays visible).
		sessionStorage.setItem(STORAGE_KEY, token);
		url.searchParams.delete("token");
		window.history.replaceState(window.history.state, "", url.toString());
	} catch {
		// Nothing else to do; the page falls back to the URL param.
	}
}

/** The invite token from the URL if still there, otherwise from the stash. */
export function readInviteToken(fromUrl: string | null): string | null {
	if (fromUrl) return fromUrl;
	try {
		return sessionStorage.getItem(STORAGE_KEY);
	} catch {
		return null;
	}
}

export function clearInviteToken(): void {
	try {
		sessionStorage.removeItem(STORAGE_KEY);
	} catch {
		// Storage blocked: nothing was stashed.
	}
}
